import React, { useMemo } from 'react';
import type { SecurityEventItem } from '../../api-utils/security_events';
import type { ThreatEventItem } from '../../api-utils/threats';
import { flattenIdList } from '../../lib/funnel';
import { formatUtcDateTime } from '../../lib/time';
import { isDuplicateEvent } from '../../api-utils/security_events';

interface AttackPathStepperProps {
  threat: ThreatEventItem | null;
  events: SecurityEventItem[];
  mitreTags?: string[];
  /** When set, steps whose ids are not in the set render muted. */
  focusEventIds?: Set<string> | null;
  focusLabel?: string;
  /** Optional subtitle per security-event id (e.g. owning action in a campaign). */
  stepNotes?: Record<string, string>;
  emptyHint?: string;
}

function eventMs(event: SecurityEventItem): number {
  const ms = Date.parse(event.event_time || event.ingestion_time || '');
  return Number.isFinite(ms) ? ms : 0;
}

function formatOffset(ms: number, t0: number): string {
  const delta = Math.max(0, ms - t0);
  const secs = Math.round(delta / 1000);
  if (secs < 60) return `T0+${secs}s`;
  const mins = Math.round(secs / 60);
  return `T0+${mins}m`;
}

function formatStepTime(iso?: string): string {
  return formatUtcDateTime(iso, true);
}

const AttackPathStepper: React.FC<AttackPathStepperProps> = ({
  threat,
  events,
  mitreTags,
  focusEventIds,
  focusLabel,
  stepNotes,
  emptyHint,
}) => {
  const ordered = useMemo(
    () => [...events].sort((a, b) => eventMs(a) - eventMs(b)),
    [events]
  );
  const t0 =
    Date.parse(threat?.first_event_time || '') ||
    (ordered.length ? eventMs(ordered[0]) : 0);

  const tagLine = useMemo(() => {
    const tags = (mitreTags || []).map((tag) => String(tag || '').trim()).filter(Boolean);
    return tags.length > 0 ? tags.join(', ') : '';
  }, [mitreTags]);

  if (!threat) {
    return <p className="c3-attack-empty">{emptyHint || 'Set a scope to see an attack path.'}</p>;
  }

  const linkedIds = flattenIdList(threat.security_event_ids);
  const steps = ordered.filter((event) => linkedIds.length === 0 || linkedIds.includes(event._id));

  if (steps.length === 0) {
    return (
      <p className="c3-attack-empty">
        {emptyHint || 'No security events linked to this threat.'}
      </p>
    );
  }

  const filtering = focusEventIds != null;

  return (
    <div className="c3-attack-body">
      {tagLine ? (
        <div className="c3-attack-mitre">
          <span className="c3-attack-mitre-k">MITRE TAGS:</span> {tagLine}
        </div>
      ) : null}
      {filtering && focusLabel && (
        <p className="c3-attack-filter">Events mentioning {focusLabel}</p>
      )}
      <ol className="c3-stepper">
        {steps.map((event, index) => {
          const source =
            event.actor_identifier || event.actor_name || event.actor_id || event.source_ip || '—';
          const target =
            event.resource_identifier ||
            event.target_identifier ||
            event.resource_name ||
            event.target_name ||
            '—';
          const technique = [event.event_type, event.action].filter(Boolean).join(' · ') || 'event';
          const muted = focusEventIds != null && !focusEventIds.has(event._id);
          return (
            <li
              key={event._id}
              className={`c3-stepper-row${muted ? ' is-muted' : ''}`}
            >
              <span className="c3-stepper-idx">{index + 1}</span>
              <div className="c3-stepper-copy">
                <div className="c3-stepper-title">
                  {technique}
                  {isDuplicateEvent(event.is_duplicate) ? (
                    <span className="c3-stepper-badge is-dup">dup</span>
                  ) : (
                    <span className="c3-stepper-badge">new</span>
                  )}
                </div>
                <div className="c3-stepper-edge">
                  {source} → {target}
                </div>
                <div className="c3-stepper-time">
                  {formatStepTime(event.event_time)} · {formatOffset(eventMs(event), t0)}
                </div>
                {stepNotes?.[event._id] ? (
                  <div className="c3-stepper-note">{stepNotes[event._id]}</div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default AttackPathStepper;
